// Non-visual logic behind the Société "Adjust Points" dialog: which point buckets can be
// moved, what the backend accepts, and whether a requested debit can actually be covered
// by the member's balance.
//
// Debits are checked per bucket rather than against the total, because IBS settles each
// bucket separately (`PNTTODBT` vs `BNSTODBT`, `SCTODBT` vs `SCBSTODBT`). Debiting more
// than a single bucket holds is rejected by IBS with a 422 `IAB` (insufficient account
// balance), even when the member's overall balance would have covered it.
import { formatDecimal } from './numberFormatters';

/** Direction of a manual points adjustment. */
export type AdjustmentAction = 'credit' | 'debit';

/** The adjustment payload keys the dialog can populate. */
export type AdjustmentFieldKey =
  | 'basePoints'
  | 'bonusPoints'
  | 'statusCredits'
  | 'bonusStatusCredits'
  | 'numberOfNights';

/**
 * A bucket the admin can credit or debit, together with the backend limit for a single
 * adjustment and how to read its current balance off an account.
 */
export interface AdjustmentField {
  key: AdjustmentFieldKey;
  label: string;
  /** Upper bound enforced by the svc-lux-loyalty public adjustment validator. */
  max: number;
  /**
   * Available balance for this bucket, or `undefined` when the account payload predates
   * the base/bonus breakdown.
   */
  getBalance: (account: App.LuxLoyaltyAccount) => number | undefined;
}

/**
 * The five buckets, in the order they are shown in the dialog. Limits mirror
 * `adjustmentsRequestValidator` in svc-lux-loyalty — keep them in sync.
 *
 * Note the asymmetry on status credits: the payload key for the *base* bucket is
 * `statusCredits` (not `baseStatusCredits`), while the balance it must be checked
 * against is `account.baseStatusCredits`.
 */
export const ADJUSTMENT_FIELDS: ReadonlyArray<AdjustmentField> = [
  { key: 'basePoints', label: 'Base Points', max: 500000, getBalance: (account) => account.basePoints },
  { key: 'bonusPoints', label: 'Bonus Points', max: 500000, getBalance: (account) => account.bonusPoints },
  { key: 'statusCredits', label: 'Status Credits', max: 1500, getBalance: (account) => account.baseStatusCredits },
  {
    key: 'bonusStatusCredits',
    label: 'Bonus Status Credits',
    max: 1500,
    getBalance: (account) => account.bonusStatusCredits,
  },
  { key: 'numberOfNights', label: 'Nights', max: 30, getBalance: (account) => account.numberOfNights },
];

/** Raw dialog input, keyed by field. Values are the unparsed text field contents. */
export type AdjustmentValues = Record<AdjustmentFieldKey, string>;

/** Validation messages, keyed by field. A missing key means the field is valid. */
export type AdjustmentErrors = Partial<Record<AdjustmentFieldKey, string>>;

/** Every field starts blank. */
export const EMPTY_ADJUSTMENT_VALUES: AdjustmentValues = {
  basePoints: '',
  bonusPoints: '',
  statusCredits: '',
  bonusStatusCredits: '',
  numberOfNights: '',
};

/**
 * Buckets that share an aggregate balance when the breakdown is unavailable. Used only
 * by the degraded path — with a breakdown present each bucket is checked on its own.
 */
const AGGREGATE_GROUPS: ReadonlyArray<{
  keys: ReadonlyArray<AdjustmentFieldKey>;
  label: string;
  getTotal: (account: App.LuxLoyaltyAccount) => number;
}> = [
  { keys: ['basePoints', 'bonusPoints'], label: 'points', getTotal: (account) => account.points },
  {
    keys: ['statusCredits', 'bonusStatusCredits'],
    label: 'status credits',
    getTotal: (account) => account.statusCredits,
  },
];

/**
 * Whether the account payload carries the base/bonus split. False against an older
 * svc-lux-loyalty deploy, in which case the dialog falls back to aggregate checks.
 */
export function hasPointsBreakdown(account: App.LuxLoyaltyAccount): boolean {
  return (
    typeof account.basePoints === 'number' &&
    typeof account.bonusPoints === 'number' &&
    typeof account.baseStatusCredits === 'number' &&
    typeof account.bonusStatusCredits === 'number'
  );
}

/**
 * Available balance to check a bucket against, and to show beneath its input.
 *
 * Without the breakdown only nights have a trustworthy per-bucket balance — the point and
 * status credit buckets fall back to the combined check in `validateAdjustment`.
 *
 * @returns the balance, or `undefined` when it cannot be determined
 */
export function getFieldBalance(account: App.LuxLoyaltyAccount, field: AdjustmentField): number | undefined {
  if (!hasPointsBreakdown(account) && field.key !== 'numberOfNights') return undefined;

  return field.getBalance(account);
}

/**
 * Parse a text field into an adjustment amount.
 *
 * @returns the amount, or `undefined` when the field is blank or not a whole number —
 *          callers distinguish the two via `isMalformedAmount`.
 */
export function parseAdjustmentValue(rawValue: string): number | undefined {
  const trimmed = rawValue.trim();
  if (!trimmed) return undefined;

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) return undefined;

  return parsed;
}

/** True when the field has content that is not a valid whole, non-negative amount. */
export function isMalformedAmount(rawValue: string): boolean {
  return rawValue.trim() !== '' && parseAdjustmentValue(rawValue) === undefined;
}

/**
 * Validate every field against the backend limits and — for debits — the member's
 * available balance.
 *
 * With a breakdown present each bucket is checked against its own balance. Without one,
 * the two point buckets are checked against `account.points` combined, and the two status
 * credit buckets against `account.statusCredits` combined; that is the tightest bound
 * available and still prevents the most common IAB case.
 *
 * @returns a message per invalid field; an empty object when everything is acceptable
 */
export function validateAdjustment(
  values: AdjustmentValues,
  action: AdjustmentAction,
  account: App.LuxLoyaltyAccount,
): AdjustmentErrors {
  const errors: AdjustmentErrors = {};
  const breakdownAvailable = hasPointsBreakdown(account);

  for (const field of ADJUSTMENT_FIELDS) {
    const rawValue = values[field.key];
    if (!rawValue.trim()) continue;

    const amount = parseAdjustmentValue(rawValue);
    if (amount === undefined) {
      errors[field.key] = 'Must be a whole number of 0 or more';
      continue;
    }

    if (amount > field.max) {
      errors[field.key] = `Must not exceed ${formatDecimal(field.max)}`;
      continue;
    }

    if (action !== 'debit') continue;

    const balance = getFieldBalance(account, field);
    if (balance !== undefined && amount > balance) {
      errors[field.key] = `Exceeds available balance (${formatDecimal(balance)})`;
    }
  }

  if (action === 'debit' && !breakdownAvailable) {
    applyAggregateDebitErrors(values, account, errors);
  }

  return errors;
}

/**
 * Degraded-path guard: without a breakdown we can still reject a debit whose combined
 * buckets exceed the aggregate balance. Only fills gaps — a field that already failed a
 * limit or format check keeps its more specific message.
 */
function applyAggregateDebitErrors(
  values: AdjustmentValues,
  account: App.LuxLoyaltyAccount,
  errors: AdjustmentErrors,
): void {
  for (const group of AGGREGATE_GROUPS) {
    const total = group.keys.reduce((sum, key) => sum + (parseAdjustmentValue(values[key]) ?? 0), 0);
    const available = group.getTotal(account);
    if (total <= available) continue;

    const message = `Combined debit exceeds available ${group.label} (${formatDecimal(available)})`;
    for (const key of group.keys) {
      if (!errors[key] && values[key].trim()) {
        errors[key] = message;
      }
    }
  }
}

/**
 * Balance a bucket would hold once the adjustment is applied, for the inline
 * `1,200 → 700` preview.
 *
 * @returns the projected balance, or `undefined` when the current balance is unknown or
 *          the input is blank/invalid
 */
export function getProjectedBalance(
  balance: number | undefined,
  rawValue: string,
  action: AdjustmentAction,
): number | undefined {
  const amount = parseAdjustmentValue(rawValue);
  if (balance === undefined || amount === undefined || amount === 0) return undefined;

  return action === 'credit' ? balance + amount : balance - amount;
}

/**
 * Collapse the dialog inputs into the adjustment request payload, dropping blank and zero
 * fields so the request only carries buckets the admin actually intends to move.
 */
export function buildAdjustmentAmounts(values: AdjustmentValues): Partial<Record<AdjustmentFieldKey, number>> {
  const amounts: Partial<Record<AdjustmentFieldKey, number>> = {};

  for (const field of ADJUSTMENT_FIELDS) {
    const amount = parseAdjustmentValue(values[field.key]);
    if (amount !== undefined && amount > 0) {
      amounts[field.key] = amount;
    }
  }

  return amounts;
}

/**
 * Whether the form carries at least one bucket to move. Nights count on their own — IBS
 * settles them as their own bucket, so adjusting only the stay-night count behind tier
 * progression is a valid request.
 */
export function hasAdjustableAmount(values: AdjustmentValues): boolean {
  return Object.keys(buildAdjustmentAmounts(values)).length > 0;
}
